import React from 'react'
import Featured from '@truphone/core-components/src/components/form/Featured'
import OptionsSelection from '@truphone/core-components/src/components/form/OptionsSelection'
import Select from '@truphone/core-components/src/components/form/Select'
import { Controller, useFormContext } from 'react-hook-form'
import { PrimaryButton } from '@truphone/buttons'
import { SliderCalculator } from '@truphone/slider-components'
import MultiSelectEditable from '@truphone/core-components/src/components/form/MultiSelectEditable'
import { EidField } from './EidField'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import CountryList from '@truphone/core-components/src/components/form/CountryList'

export function FormField(props) {
  const {
    register,
    watch,
    getValues,
    control,
    formState: { errors }
  } = useFormContext()

  const value = watch(props.name)
  const filled = !!value
  const valid = !errors[props.name]

  const replaceFieldValues = (content) => {
    let newContent = content
    const match = content.match(/\{(.*)\}/)

    if (match && match.length > 1) {
      const values = getValues()
      const value = values[Object.getOwnPropertyNames(values).find((x) => x.startsWith(match[1]))]
      newContent = content.replace(match[0], value)
    }

    return newContent
  }

  const rules = {
    required: props.required && (props.requiredMessage || true),
    pattern: props.pattern && {
      value: new RegExp(props.pattern),
      message: props.patternMessage
    }
  }

  const label = (
    <>
      {' '}
      {props.label && (
        <label className="mb-2 text-sm inline-block">
          {props.label} {props.required && '*'}
        </label>
      )}
    </>
  )

  return (
    <div className="mt-6">
      {props.type === 'select' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => (
            <>
              {label}
              <Select
                className={`border ${!valid ? 'border-pink' : ''} border-grey-200 rounded-lg`}
                options={props.options}
                {...field}
              />
            </>
          )}
          rules={rules}
          shouldUnregister={true}
        />
      )}

      {props.type === 'options' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field: { value, onChange } }) => (
            <>
              {label}
              <OptionsSelection
                style={props.style}
                options={props.options}
                value={value}
                onChange={onChange}
              />
            </>
          )}
          shouldUnregister={true}
        />
      )}

      {props.type === 'featured' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Featured
              collapsed={props.collapsed}
              label={props.label}
              required={props.required}
              articles={props.articles}
              options={props.options}
              value={value}
              onChange={onChange}
            />
          )}
          shouldUnregister={true}
        />
      )}

      {props.type === 'end' && (
        <div
          className="p-margin link-blue list-numbers list-square"
          dangerouslySetInnerHTML={{ __html: replaceFieldValues(props.content) }}
        />
      )}

      {props.type === 'button' && (
        <>
          {!props.automationUser && props.enableRecaptcha && (
            <div>
              <Controller
                name={'hCaptureToken::'}
                control={control}
                render={({ field: { onChange } }) => (
                  <HCaptcha
                    sitekey="86214b44-a6d8-4b52-a41b-949907dea841"
                    onVerify={(token) => onChange(token)}
                  />
                )}
                rules={{ required: props.recaptchaRequiredMessage }}
              />

              {errors['hCaptureToken::'] && (
                <label className={`mb-2 text-xs text-pink`}>
                  {errors['hCaptureToken::']?.message}
                </label>
              )}
            </div>
          )}

          <PrimaryButton
            className={'cursor-pointer my-10'}
            onClick={() => props.onSubmit(props.thankYouMessage)}
          >
            {props.buttonText}
          </PrimaryButton>
        </>
      )}

      {(props.type === 'date' ||
        props.type === 'time' ||
        props.type === 'text' ||
        props.type === 'email') && (
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => {
            return (
              <>
                {label}
                <input
                  type={props.type.toLowerCase()}
                  className={`border ${
                    filled && valid ? 'border-green-400' : !valid ? 'border-pink' : ''
                  } border-grey-200 px-4 py-2 rounded-lg w-full`}
                  maxLength={props.maxLength}
                  {...field}
                />
              </>
            )
          }}
          rules={rules}
          shouldUnregister={true}
        />
      )}

      {props.type === 'eid' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => {
            return (
              <>
                {label}
                <EidField
                  className={`border ${
                    filled && valid ? 'border-green-400' : !valid ? 'border-pink' : ''
                  } border-grey-200 px-4 py-2 rounded-lg w-full`}
                  {...field}
                />
              </>
            )
          }}
          rules={rules}
          shouldUnregister={true}
        />
      )}

      {props.type === 'text-area' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => (
            <>
              {label}
              <textarea
                className={`border ${
                  filled && valid ? 'border-green-400' : !valid ? 'border-pink' : ''
                } border-grey-200 px-4 py-2 rounded-lg w-full block`}
                {...field}
              />
            </>
          )}
          rules={rules}
          shouldUnregister={true}
        />
      )}

      {props.type === 'country-select' && (
        <CountryList
          {...props}
          defaultValue={props.populateIp ? props.userLocation : null}
          countryListLanguage={props.language}
        />
      )}

      {props.type === 'multi-select' && (
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => {
            let options =
              require(`../../../../core-components/src/components/form/country-json/countries-regions-${props.language.toLowerCase()}`).map(
                (x) => ({
                  text: x['Country Name'],
                  value: x['Country Name'],
                  iso: x['ISO'],
                  region: x['region']
                })
              )

            return (
              <>
                {label}
                <MultiSelectEditable
                  {...field}
                  multiple={props.multiple}
                  name={field.name}
                  placeholder={field.placeholder}
                  options={options}
                />
              </>
            )
          }}
          rules={rules}
          shouldUnregister={true}
        />
      )}

      {props.type === 'slider' && (
        <Controller
          name={props.name}
          control={control}
          defaultValue={props.options && props.options[0].value}
          render={({ field: { value, onChange } }) => (
            <>
              <h4 className="text-sm mb-4 flex">
                {props.label} {props.required && <span className="ml-1">*</span>}
              </h4>
              <SliderCalculator
                value={value}
                onChange={onChange}
                selectFieldOptions={props.options}
              />
            </>
          )}
          shouldUnregister={true}
        />
      )}

      {props.type === 'case-management-general-inquiry' && (
        <>
          <input
            {...register('case_product', { shouldUnregister: true, value: props.caseProduct })}
            type="hidden"
          />
          <input
            {...register('case_type', { shouldUnregister: true, value: props.caseType })}
            type="hidden"
          />
          <input
            {...register('case_category', { shouldUnregister: true, value: props.caseCategory })}
            type="hidden"
          />
          <input
            {...register('case_page_locale', { shouldUnregister: true, value: props.pageLocale })}
            type="hidden"
          />
        </>
      )}

      {props.type === 'case-management-provision' && (
        <>
          <input
            {...register('case_product', { shouldUnregister: true, value: props.caseProduct })}
            type="hidden"
          />
          <input
            {...register('case_type', { shouldUnregister: true, value: props.caseType })}
            type="hidden"
          />
          <input
            {...register('case_category', { shouldUnregister: true, value: props.caseCategory })}
            type="hidden"
          />
          <input
            {...register('case_reason', { shouldUnregister: true, value: props.caseReason })}
            type="hidden"
          />
          <input
            {...register('case_service_loss', {
              shouldUnregister: true,
              value: props.caseServiceLoss
            })}
            type="hidden"
          />
          <input
            {...register('case_page_locale', { shouldUnregister: true, value: props.pageLocale })}
            type="hidden"
          />
        </>
      )}

      {props.type === 'case-management-customer-fault' && (
        <>
          <input
            {...register('case_product', { shouldUnregister: true, value: props.caseProduct })}
            type="hidden"
          />
          <input
            {...register('case_type', { shouldUnregister: true, value: props.caseType })}
            type="hidden"
          />
          <input
            {...register('case_category', { shouldUnregister: true, value: props.caseCategory })}
            type="hidden"
          />
          <input
            {...register('case_reason', { shouldUnregister: true, value: props.caseReason })}
            type="hidden"
          />
          <input
            {...register('case_page_locale', { shouldUnregister: true, value: props.pageLocale })}
            type="hidden"
          />
        </>
      )}

      {errors[props.name] && (
        <label className={`mb-2 text-xs text-pink`}>{errors[props.name]?.message}</label>
      )}
    </div>
  )
}
